// vendors
import { combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, pairwise } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
// libs
import {
  CellExportFacade,
  NotebookResource,
  NotebookTypes,
  NotebookCellResource,
  ContentType,
} from '@mode/notebooks/contract';
import { NormalizedNotebookCellRunResult } from '@mode/notebooks/contract';
import { NotebookState } from '@mode/notebooks/contract';
import * as NotebookCellSelectors from './notebook-cell/legacy-notebook-cell.selectors';
import * as NotebookSelectors from './notebook/legacy-notebook.selectors';

@Injectable()
export class NotebookStateService {
  private _clearedCells$ = new BehaviorSubject<Set<string>>(new Set());

  selectedNotebookToken$: Observable<string>;
  notebookState$: Observable<NotebookState>;
  notebookResource$: Observable<NotebookResource>;

  canRestartSession$: Observable<boolean>;
  notebookType$: Observable<NotebookTypes.NotebookType>;

  cellExecuting$: Observable<boolean>;
  contentType$: Observable<NotebookTypes.ContentType>;
  isInLayout$: Observable<boolean>;
  isEditing$: Observable<boolean>;
  isFrozen$: Observable<boolean>;
  isFolded$: Observable<boolean>;
  cellToken$: Observable<string>;
  nextCellToken$: Observable<string>;
  prevCellToken$: Observable<string>;

  canFreeze$: Observable<boolean>;
  canDelete$: Observable<boolean>;
  isSelectedCellExported$: Observable<boolean>;
  isExportInProgress$: Observable<boolean>;

  hasResult$: Observable<boolean>;

  pythonNotebookCells$: Observable<[string, string][]>;
  cellContentsArray$: Observable<Record<string, string>>;
  cellContentTypesArray$: Observable<Record<string, ContentType>>;
  public readonly cellResourcesArray$: Observable<Record<string, NotebookCellResource>>;
  public readonly notebookCellTokensArray$: Observable<string[]>;
  public readonly cellLastRunTokensArray$: Observable<Record<string, string>>;
  public readonly vizCellTokensArray$: Observable<any>;
  public readonly runsResultTokensArray$: Observable<any>;
  public readonly notebookToken$: Observable<any>;

  constructor(
    private store: Store,
    private cellExportFacade: CellExportFacade
  ) {
    this.selectedNotebookToken$ = this.store.select(NotebookSelectors.selectedNotebookToken);
    this.notebookState$ = this.selectedNotebookToken$.pipe(
      switchMap((token) =>
        this.store.select(NotebookSelectors.statusBarStateByNotebook, token).pipe(distinctUntilChanged())
      )
    );

    this.notebookResource$ = this.store
      .select(NotebookSelectors.selectedNotebookResource)
      .pipe(filter((r) => r !== null));
    this.notebookType$ = this.store.select(NotebookSelectors.selectedNotebookType);

    this.cellToken$ = this.store.select(NotebookSelectors.selectedCellToken);
    this.cellExecuting$ = this.store.select(NotebookCellSelectors.selectedCellExecuting);
    this.contentType$ = this.store.select(NotebookSelectors.selectedCellContentType);
    this.nextCellToken$ = this.store.select(NotebookCellSelectors.selectedNextCellToken);
    this.prevCellToken$ = this.store.select(NotebookCellSelectors.selectedPrevCellToken);

    this.isEditing$ = this.store.select(NotebookCellSelectors.isEditing);
    this.isFrozen$ = this.store.select(NotebookCellSelectors.selectedCellFrozen);
    this.isFolded$ = this.store.select(NotebookCellSelectors.selectedCellFolded);
    this.isInLayout$ = this.store.select(NotebookCellSelectors.selectedCellIsInLayout);

    this.canRestartSession$ = this.notebookState$.pipe(map((s) => s !== NotebookState.Session));
    this.canFreeze$ = this.cellToken$.pipe(
      switchMap((cellToken) => this.store.select(NotebookCellSelectors.canFreezeByCell, cellToken))
    );
    this.canDelete$ = combineLatest([
      this.cellToken$,
      this.cellExecuting$,
      this.cellExportFacade.exportingCellToken$,
    ]).pipe(
      map(([token, executing, exportingCellToken]) => {
        return token !== null && !executing && exportingCellToken !== token;
      })
    );
    this.hasResult$ = this.store.select(NotebookCellSelectors.selectedCellHasResult());

    this.isSelectedCellExported$ = this.cellToken$.pipe(
      switchMap((token) => this.store.select(NotebookCellSelectors.isExportedByCell, token) as Observable<boolean>)
    );
    this.isExportInProgress$ = this.cellExportFacade.isExporting$;

    this.pythonNotebookCells$ = combineLatest([
      this.store.select(NotebookCellSelectors.selectedNotebookCellTokens),
      this.store.select(NotebookCellSelectors.cellContentsArray),
      this.store.select(NotebookCellSelectors.cellsContentTypesArray),
    ]).pipe(
      map(([cells, cellContentMap, contentTypeMap]) => {
        return cells
          .toArray()
          .filter((token: string) => contentTypeMap.get(token) === NotebookTypes.ContentType.Python)
          .map((token: string) => [token, cellContentMap.get(token)]);
      })
    );
    this.cellContentsArray$ = this.store.select(NotebookCellSelectors.cellContentsArray);
    this.cellResourcesArray$ = this.store.select(NotebookCellSelectors.cellResourcesArray);
    this.notebookCellTokensArray$ = this.store.select(NotebookSelectors.notebookCellTokensArray);
    this.cellLastRunTokensArray$ = this.store.select(NotebookCellSelectors.cellLastRunTokensArray);
    this.vizCellTokensArray$ = this.store.select(NotebookCellSelectors.vizCellTokensArray);
    this.runsResultTokensArray$ = this.store.select(NotebookCellSelectors.runsResultTokensArray);
    this.cellContentTypesArray$ = this.store.select(NotebookCellSelectors.cellsContentTypesArray);
    this.notebookToken$ = this.store.select(NotebookSelectors.notebookToken);
  }

  // Hide all cell outputs
  notifyOutputCleared() {
    // Get all cell tokens and mark them as cleared
    this.store
      .select(NotebookCellSelectors.selectedNotebookCellTokens)
      .pipe(
        filter((tokens) => !!tokens),
        map((tokens) => tokens.toArray() as string[])
      )
      .subscribe((cellTokens) => {
        const clearedSet = new Set(cellTokens);
        this._clearedCells$.next(clearedSet);
      });
  }

  // Show specific cell output
  resetCellOutputCleared(cellToken: string) {
    const currentCleared = this._clearedCells$.value;
    const newCleared = new Set(currentCleared);
    newCleared.delete(cellToken);
    this._clearedCells$.next(newCleared);
  }

  // Show all cell outputs
  resetAllOutputCleared() {
    this._clearedCells$.next(new Set());
  }

  // Check if specific cell output is cleared
  isCellOutputCleared$(cellToken: string): Observable<boolean> {
    return this._clearedCells$.pipe(map((clearedSet) => clearedSet.has(cellToken)));
  }

  // Check if hide output button should be enabled (true when there are visible outputs)
  canHideOutput$: Observable<boolean> = combineLatest([
    this.store.select(NotebookCellSelectors.selectedNotebookCellsExecutionCount),
    this._clearedCells$.asObservable(),
    this.store.select(NotebookCellSelectors.selectedNotebookCellTokens),
  ]).pipe(
    map(([executionCount, clearedCells, cellTokens]) => {
      if (executionCount === 0) return false; // No outputs at all

      const allCellTokens = cellTokens ? (cellTokens.toArray() as string[]) : [];
      const hasVisibleOutputs = allCellTokens.some((cellToken) => !clearedCells.has(cellToken));

      return hasVisibleOutputs;
    })
  );

  // Global hasOutput$ - kept for compatibility
  hasOutput$: Observable<boolean> = combineLatest([
    this.store.select(NotebookCellSelectors.selectedNotebookCellsExecutionCount),
    this._clearedCells$.asObservable(),
  ]).pipe(map(([executionCount, clearedCells]) => executionCount > 0 && clearedCells.size === 0));

  canRunCell$ = (notebookToken: any) =>
    this.store.select(NotebookCellSelectors.createCanCellRunSelector(notebookToken));

  isCellEditing$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createIsCellEditingSelector(cellToken));

  isMarkdownViewMode$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createMarkdownViewModeSelector(cellToken));

  createCellExecutionCountSelector$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createCellExecutionCountSelector(cellToken));

  createCellAceLangSelector$ = (cellToken: string) =>
    this.store.select(NotebookCellSelectors.createCellAceLangSelector(cellToken));

  getIsCellExported$(cellToken: string): Observable<boolean> {
    return this.store.select(NotebookCellSelectors.isExportedByCell, cellToken);
  }

  createLastResultsByCellSelector(cellToken: string): Observable<NormalizedNotebookCellRunResult[]> {
    return this.store.select(NotebookCellSelectors.createLastResultsByCellSelector(cellToken));
  }
}
